import { useState, useEffect, useRef } from 'react';
import './DevarshAgent.css';
import AIService from './AIService';
import { commonQuestions } from '../data/devarsh-knowledge-base.js';

const STORAGE_KEY = 'brijChatSession';
const LOG_KEY = 'brijQuestions';
// Privacy/staleness rule: a saved chat is only restored if the visitor was
// active within the last 24 hours. Older sessions and logged questions are
// deleted, so returning visitors get a clean slate instead of old history.
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// Three short starters shown under the greeting. Short labels keep the whole
// first screen (greeting, starters, audience chips, input) visible at once.
const STARTERS = [
  { label: 'His biggest wins', question: 'What are the biggest things Devarsh has shipped?' },
  { label: 'Why hire him?', question: 'Why should I hire Devarsh?' },
  { label: 'Is he available?', question: "What's Devarsh's availability and work authorization?" },
];

const purgeExpiredStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      if (!saved || !saved.savedAt || Date.now() - saved.savedAt > SESSION_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    const logs = JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
    const fresh = logs.filter((l) => l && l.timestamp && Date.now() - new Date(l.timestamp).getTime() <= SESSION_TTL_MS);
    if (fresh.length !== logs.length) localStorage.setItem(LOG_KEY, JSON.stringify(fresh));
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(LOG_KEY); } catch { /* storage unavailable */ }
  }
};

// Logs user questions/responses to localStorage for later review. Kept
// deliberately simple (no backend) - just a lightweight local audit trail.
const logUserQuestion = (question, response) => {
  try {
    const timestamp = new Date().toISOString();
    const logEntry = { question, response, timestamp, sessionId: Date.now() };
    const existingLogs = JSON.parse(localStorage.getItem('brijQuestions') || '[]');
    existingLogs.push(logEntry);
    if (existingLogs.length > 50) existingLogs.splice(0, existingLogs.length - 50);
    localStorage.setItem('brijQuestions', JSON.stringify(existingLogs));
  } catch (error) {
    // Silent - logging failures shouldn't break the chat experience.
  }
};

const AUDIENCE_CARDS = [
  { id: 'recruiter', variant: 'audience', icon: '👔', label: 'Recruiter', desc: 'Screening for a role', question: "I'm a recruiter reviewing Dev's profile" },
  { id: 'hiring-manager', variant: 'audience', icon: '🏢', label: 'Hiring Manager', desc: 'Evaluating for your team', question: "I'm a hiring manager evaluating Dev for a role" },
  { id: 'founder', variant: 'audience', icon: '🚀', label: 'Founder / Startup', desc: 'Early-stage, moving fast', question: "I'm a founder or from an early-stage startup" },
  { id: 'other', variant: 'audience', icon: '👋', label: 'Just Exploring', desc: 'Curious, student, or peer', question: "I'm exploring for other reasons" },
];

const TOPIC_CARDS = {
  recruiter: [
    { label: 'Open to work?',      question: 'Is Dev open to new opportunities and what roles is he looking for?' },
    { label: 'Work authorization', question: "What is Dev's visa and work authorization status?" },
    { label: 'Recent experience',  question: "What is Dev's most recent work experience?" },
    { label: 'Core skills',        question: "What are Dev's core product management skills and tools?" },
    { label: 'Resume / contact',   question: "How can I download Dev's resume or contact him?" },
  ],
  'hiring-manager': [
    { label: 'Key metrics & impact', question: "What are Dev's key metrics and measurable business impact?" },
    { label: 'Why hire Dev?',        question: 'Why should I hire Dev over other candidates?' },
    { label: 'Leadership style',     question: "What is Dev's leadership and management style?" },
    { label: 'Best projects',        question: "What are Dev's best and most impactful projects?" },
    { label: 'Book a 1:1',           question: 'How can I book a call with Dev?' },
  ],
  founder: [
    { label: '0→1 experience?',    question: 'Has Dev built products from zero to one?' },
    { label: 'AI / agentic work',  question: "Tell me about Dev's agentic AI and LLM product work" },
    { label: 'How fast he ships',  question: 'How quickly does Dev ship and execute on products?' },
    { label: 'His superpowers',    question: "What are Dev's unique superpowers as a product builder?" },
    { label: 'Book a 1:1',         question: 'How can I connect with Dev or book a call?' },
  ],
  other: [
    { label: 'Who is Devarsh?',    question: 'Who is Devarsh Wali? Give me a quick overview.' },
    { label: 'Experience overview', question: "What is Dev's professional background and experience?" },
    { label: 'Key achievements',   question: "What are Dev's biggest professional achievements?" },
    { label: 'Fun facts',          question: 'What are some interesting facts about Dev?' },
    { label: 'Book a 1:1',         question: 'How can I connect with Dev?' },
  ],
};

const FOLLOWUP_CARDS = {
  recruiter: [
    { label: 'When can he start?',   question: 'When is Dev available to start a new role?' },
    { label: 'Salary expectations?', question: "What are Dev's salary expectations?" },
    { label: 'His biggest wins',     question: "What are Dev's most impressive career achievements?" },
    { label: 'Book a 1:1',            question: 'How can I contact Dev directly?' },
  ],
  'hiring-manager': [
    { label: 'Specific company?',  question: "Tell me about Dev's experience at a specific company" },
    { label: 'His metrics',        question: 'What are the specific numbers and metrics from his career?' },
    { label: 'Strengths & gaps',   question: "What are Dev's key strengths and growth areas?" },
    { label: 'Book a 1:1',         question: 'How can I book a call with Dev?' },
  ],
  founder: [
    { label: 'His startup story',  question: 'Tell me about Muvely and his startup founder experience' },
    { label: 'Startup mindset',    question: 'Does Dev have a startup and ownership mindset?' },
    { label: 'His AI builds',      question: 'What AI tools and MVPs has Dev personally built?' },
    { label: 'Book a 1:1',         question: 'How can I connect with Dev?' },
  ],
  other: [
    { label: 'Magic & hobbies',   question: 'Tell me about Dev being a magician and his hobbies' },
    { label: 'His AI projects',   question: 'What AI and tech projects has Dev built for fun?' },
    { label: 'His biggest numbers', question: "What are Dev's most impressive career metrics?" },
    { label: 'Contact Dev',       question: 'How can I reach Dev or connect with him?' },
  ],
};

// Maps the internal topic keys AIService tags each retrieval with into
// human-readable pill labels shown under an answer - a small transparency
// touch that shows what the RAG retrieval actually pulled in.
const TOPIC_LABELS = {
  'umbc-pm': 'UMBC', presence: 'Presence', peerteach: 'PeerTeach',
  harbinger: 'Harbinger Lane', barclays: 'Barclays', byjus: "BYJU'S", opportune: 'Opportune',
  skills: 'Skills', education: 'Education', personality: 'Personality', leadership: 'Leadership',
  projects: 'Projects', hiring: 'Hiring Fit', availability: 'Availability', achievements: 'Achievements',
  experience: 'Experience', experimentation: 'A/B Testing', revenue: 'Revenue', recommendations: 'Recommendations',
  languages: 'Languages', industry: 'Industry', salary: 'Compensation', years: 'Experience',
  'companies-count': 'Career History', 'why-pm': 'Career Journey', nationality: 'Background',
  proudest: 'Achievements', unique: 'What Makes Him Unique', 'current-status': 'Availability',
  strengths: 'Strengths', impact: 'Impact', age: 'Personal', 'ai-builder': 'AI Projects',
  'magic-detail': 'Magic & Hobbies',
};

const DevarshAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState(null);

  const aiServiceRef = useRef(null);
  if (!aiServiceRef.current) aiServiceRef.current = new AIService();
  const aiService = aiServiceRef.current;

  const messagesEndRef = useRef(null);
  const requestIdRef = useRef(0);
  const greetedRef = useRef(false);
  const msgIdRef = useRef(0);
  const abortRef = useRef(null);

  // Restore a prior session so returning visitors pick up where they left
  // off, instead of Brij "forgetting" everything on every page reload.
  useEffect(() => {
    purgeExpiredStorage();
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && Array.isArray(saved.messages) && saved.messages.length > 0) {
        setMessages(saved.messages);
        setSelectedAudience(saved.selectedAudience || null);
        setUserRole(saved.userRole || '');
        setQuickReplies(saved.quickReplies || []);
        msgIdRef.current = saved.messages.reduce((max, m) => Math.max(max, m.id || 0), 0);
        greetedRef.current = true;
      }
    } catch {
      // Corrupt/blocked localStorage - just start fresh.
    }
  }, []);

  useEffect(() => {
    if (messages.length === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, selectedAudience, userRole, quickReplies, savedAt: Date.now() }));
    } catch {
      // Storage full or unavailable (private browsing) - non-fatal.
    }
  }, [messages, selectedAudience, userRole, quickReplies]);

  // NOTE: effect callbacks must return nothing (or a cleanup function). The
  // old version returned scrollIntoView()'s result directly; newer Chrome
  // returns a Promise there, which React then tried to call as a cleanup
  // function on the next render -> "TypeError: i is not a function" -> crash.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const addMessage = (msg) => {
    const id = ++msgIdRef.current;
    setMessages((prev) => [...prev, { ...msg, id }]);
    return id;
  };

  const updateMessage = (id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const queueFollowups = (explicitCards) => {
    if (explicitCards && explicitCards.length > 0) {
      setTimeout(() => setQuickReplies(explicitCards), 400);
    } else if (selectedAudience) {
      setTimeout(() => setQuickReplies(FOLLOWUP_CARDS[selectedAudience] || []), 400);
    }
  };

  // Core exchange: try the instant/canned path first (greetings, jokes,
  // card-driven questions), and only fall through to a live, streamed Groq
  // answer when nothing matches. Cancels any in-flight exchange first so
  // rapid-fire messages don't race each other.
  const runExchange = async (questionText, nextCardsIfHandled) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const reqId = ++requestIdRef.current;

    setIsTyping(true);
    setQuickReplies([]);

    // Small delay so "thinking" is visible even for instant replies - keeps
    // the pacing feeling alive rather than like an instant lookup table.
    await new Promise((r) => setTimeout(r, 450));
    if (reqId !== requestIdRef.current) return;

    const instant = aiService.getInstantResponse(questionText, userRole, messages);

    if (instant !== null) {
      setIsTyping(false);
      // Canned answers don't run through retrieveRelevantContext, so run it
      // here purely for its side effect of populating lastMatchedTopics -
      // that's what lets us attach a grounded topic card to canned replies
      // too, not just streamed ones. Wrapped in try/catch (matching the
      // streaming branch below) so a future bad topic-card lookup degrades
      // to "reply with no card" instead of an unhandled rejection.
      let card = null;
      try {
        aiService.retrieveRelevantContext(questionText);
        card = aiService.getTopicCard();
      } catch (error) {
        console.error('Topic card lookup failed for instant response:', error);
      }
      addMessage({ type: 'agent', text: instant, topics: aiService.lastMatchedTopics || [], card });
      logUserQuestion(questionText, instant);
      queueFollowups(nextCardsIfHandled);
      return;
    }

    setIsTyping(false);
    setIsStreaming(true);
    const id = addMessage({ type: 'agent', text: '', streaming: true, topics: [] });

    try {
      const finalText = await aiService.streamLLMResponse(
        questionText,
        userRole,
        messages,
        (_delta, full) => {
          if (reqId !== requestIdRef.current) return;
          updateMessage(id, { text: full });
        },
        controller.signal
      );
      if (reqId !== requestIdRef.current) return;
      const card = aiService.getTopicCard();
      updateMessage(id, { text: finalText, streaming: false, topics: aiService.lastMatchedTopics || [], card });
      logUserQuestion(questionText, finalText);
      queueFollowups(nextCardsIfHandled);
    } catch (error) {
      if (reqId !== requestIdRef.current) return;
      updateMessage(id, {
        text: "I'm having trouble connecting right now. Please try again in a moment!",
        streaming: false,
      });
    } finally {
      if (reqId === requestIdRef.current) setIsStreaming(false);
    }
  };

  const handleCardClick = (card) => {
    addMessage({ type: 'user', text: card.icon ? `${card.icon} ${card.label}` : card.label });
    const isAudienceCard = AUDIENCE_CARDS.some((c) => c.id === card.id);
    if (isAudienceCard) {
      setSelectedAudience(card.id);
      runExchange(card.question, TOPIC_CARDS[card.id] || []);
    } else {
      runExchange(card.question, selectedAudience ? FOLLOWUP_CARDS[selectedAudience] : []);
    }
  };

  const handleStarterClick = (question) => {
    addMessage({ type: 'user', text: question });
    runExchange(question, null);
  };

  const handleSend = () => {
    if (!inputValue.trim() || isStreaming) return;
    const text = inputValue.trim();
    addMessage({ type: 'user', text });
    setInputValue('');
    runExchange(text, null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyMessage = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  // Other parts of the page (the proof feed) can ask Brij a question by
  // dispatching a 'brij:ask' event. Kept in a ref so the single listener
  // always calls the latest handler (fresh state) without re-subscribing.
  const askRef = useRef(null);
  askRef.current = (question) => {
    if (!question || isStreaming) return;
    greetedRef.current = true;
    setIsOpen(true);
    handleStarterClick(question);
  };
  useEffect(() => {
    const onAsk = (e) => askRef.current?.(e.detail?.question);
    window.addEventListener('brij:ask', onAsk);
    return () => window.removeEventListener('brij:ask', onAsk);
  }, []);

  // Silent nudges for the launcher: an "unread" badge until first open (resets
  // after 24h) and an occasional "typing..." bubble, like a message is coming.
  const [fabSeen, setFabSeen] = useState(() => {
    try {
      const t = Number(localStorage.getItem('brijFabSeen') || 0);
      return t > 0 && Date.now() - t < SESSION_TTL_MS;
    } catch { return false; }
  });
  const [peek, setPeek] = useState(false);
  useEffect(() => {
    if (isOpen || fabSeen) return undefined;
    const timers = [];
    [4500, 26000, 58000].forEach((at) => {
      timers.push(setTimeout(() => setPeek(true), at));
      timers.push(setTimeout(() => setPeek(false), at + 3600));
    });
    return () => { timers.forEach(clearTimeout); setPeek(false); };
  }, [isOpen, fabSeen]);

  const toggleChat = () => {
    if (!fabSeen) {
      setFabSeen(true);
      try { localStorage.setItem('brijFabSeen', String(Date.now())); } catch { /* non-fatal */ }
    }
    setIsOpen((open) => {
      const nowOpen = !open;
      if (nowOpen && !greetedRef.current) {
        greetedRef.current = true;
        setTimeout(() => {
          addMessage({
            type: 'agent',
            text: "Hey, I'm Brij, Dev's AI twin. Ask me anything about his work, or pick one below.",
            topics: [],
          });
          setQuickReplies(AUDIENCE_CARDS);
        }, 300);
      }
      return nowOpen;
    });
  };

  const startNewConversation = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setQuickReplies([]);
    setSelectedAudience(null);
    setUserRole('');
    msgIdRef.current = 0;
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setTimeout(() => {
      addMessage({
        type: 'agent',
        text: "Fresh start! I'm Brij, Devarsh's AI twin. What would you like to know?",
        topics: [],
      });
      setQuickReplies(AUDIENCE_CARDS);
    }, 200);
  };

  const hasUserMessage = messages.some((m) => m.type === 'user');

  // Renders the structured card returned by AIService.getTopicCard() as a
  // visual overlay beneath a message - grounded in the same knowledge-base
  // data the text answer came from, so the card can never say something the
  // text didn't already say.
  const renderTopicCard = (card) => {
    if (!card) return null;

    if (card.kind === 'company') {
      return (
        <div className="chat-card chat-card-company">
          <div className="chat-card-header">
            <span className="chat-card-icon">🏢</span>
            <div className="chat-card-title-group">
              <div className="chat-card-title">{card.company}</div>
              <div className="chat-card-subtitle">{card.role}</div>
            </div>
            {card.isVolunteer && <span className="chat-card-badge volunteer">Volunteer</span>}
          </div>
          <div className="chat-card-meta">
            <span>{card.industry}</span>
            <span className="chat-card-dot">·</span>
            <span>{card.period}</span>
          </div>
          {card.metrics && card.metrics.length > 0 && (
            <div className="chat-card-metrics">
              {card.metrics.map((m, i) => (
                <div className="chat-card-metric" key={i}>
                  <div className="metric-value">{String(m.value)}</div>
                  <div className="metric-label">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {card.tools && card.tools.length > 0 && (
            <div className="chat-card-chips">
              {card.tools.map((t, i) => (
                <span className="chat-card-chip" key={i}>{t}</span>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (card.kind === 'skills') {
      return (
        <div className="chat-card chat-card-skills">
          <div className="chat-card-header">
            <span className="chat-card-icon">🛠️</span>
            <div className="chat-card-title">Skills snapshot</div>
          </div>
          {card.groups.map((g, i) => (
            <div className="chat-card-skill-group" key={i}>
              <div className="skill-group-label">{g.label}</div>
              <div className="chat-card-chips">
                {g.items.map((it, j) => (
                  <span className="chat-card-chip" key={j}>{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (card.kind === 'projects') {
      return (
        <div className="chat-card chat-card-projects">
          <div className="chat-card-header">
            <span className="chat-card-icon">🚀</span>
            <div className="chat-card-title">Featured projects</div>
          </div>
          {card.items.map((p, i) => (
            <div className="chat-card-project" key={i}>
              <div className="project-name">{p.name}</div>
              <div className="project-desc">{p.description}</div>
              {p.highlights && p.highlights.length > 0 && (
                <div className="chat-card-chips">
                  {p.highlights.map((h, j) => (
                    <span className="chat-card-chip subtle" key={j}>{h}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (card.kind === 'stats') {
      return (
        <div className="chat-card chat-card-stats">
          <div className="chat-card-header">
            <span className="chat-card-icon">📊</span>
            <div className="chat-card-title">{card.title || 'Standout numbers'}</div>
          </div>
          <div className="chat-card-stat-grid">
            {card.stats.map((s, i) => (
              <div className="chat-card-stat" key={i}>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`devarsh-agent ${isOpen ? 'chat-open' : ''}`}>
      <div className={`agent-icon ${isOpen ? 'open' : ''}`} onClick={toggleChat}>
        <div className={`brij-fab ${isOpen ? 'is-open' : ''}`} role="button" aria-label="Open chat with Brij">
          <span className="fab-ring" aria-hidden="true"></span>
          <span className="fab-body">
            {/* chat bubble with a typing indicator and an AI spark */}
            <svg className="fab-bubble" viewBox="0 0 48 48" aria-hidden="true">
              <defs>
                <linearGradient id="fabg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
                  <stop offset="1" stopColor="#c9c2ff" stopOpacity="0.85" />
                </linearGradient>
              </defs>
              <path d="M10 8h28a6 6 0 0 1 6 6v15a6 6 0 0 1-6 6H24l-9 7v-7h-5a6 6 0 0 1-6-6V14a6 6 0 0 1 6-6z" fill="url(#fabg)" />
              <circle className="fab-dot d1" cx="15" cy="22" r="2.6" fill="#4b3fd6" />
              <circle className="fab-dot d2" cx="24" cy="22" r="2.6" fill="#4b3fd6" />
              <circle className="fab-dot d3" cx="33" cy="22" r="2.6" fill="#4b3fd6" />
              <path className="fab-spark" d="M41 2l1.7 4.3L47 8l-4.3 1.7L41 14l-1.7-4.3L35 8l4.3-1.7z" fill="#ff7ab6" />
            </svg>
            <svg className="fab-x" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
          </span>
          {!fabSeen && !isOpen && <span className="fab-unread" aria-hidden="true">1</span>}
          {peek && !isOpen && (
            <span className="fab-peek" aria-hidden="true">
              <i></i><i></i><i></i>
            </span>
          )}
        </div>
        <div className="agent-badge">
          <span>Chat with Brij</span>
          <div className="badge-subtitle">Dev's AI Twin</div>
        </div>
      </div>

      {isOpen && (
        <div className={`chat-container ${isExpanded ? 'expanded' : ''}`}>
          <div className="chat-header">
            <div className="header-info">
              <div className="agent-status">
                <div className="status-dot online"></div>
                <div className="agent-details">
                  <span className="agent-name">Brij</span>
                  <span className="agent-role">Dev's AI Twin · Ask me anything</span>
                </div>
              </div>
              {userRole && (
                <div className="header-subtitle">🎯 Tailored for: {userRole}</div>
              )}
            </div>
            <div className="header-actions">
              <button className="header-btn" onClick={startNewConversation} title="New conversation" aria-label="New conversation">↺</button>
              <button
                className="header-btn"
                onClick={() => setIsExpanded((e) => !e)}
                title={isExpanded ? 'Collapse panel' : 'Expand panel'}
                aria-label="Toggle panel size"
              >
                {isExpanded ? '⤡' : '⤢'}
              </button>
              <button className="close-btn" onClick={() => setIsOpen(false)} aria-label="Close chat">×</button>
            </div>
          </div>

          <div className="chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                {message.type === 'agent' && (
                  <div className="message-avatar">
                    <div className="mini-orb">
                      <span className="mini-orb-emoji">🔮</span>
                    </div>
                  </div>
                )}
                <div className="message-bubble">
                  <div className="message-content">
                    {message.text.split('\n').map((line, i) => (
                      <div key={i} className={line.startsWith('**') && line.endsWith('**') ? 'message-header-text' : ''}>
                        {line.replace(/\*\*(.*?)\*\*/g, '$1')}
                      </div>
                    ))}
                    {message.streaming && <span className="stream-cursor" />}
                  </div>
                  {message.type === 'agent' && !message.streaming && message.card && (
                    renderTopicCard(message.card)
                  )}
                  {message.type === 'agent' && !message.streaming && message.text && (
                    <div className="message-footer">
                      {message.topics && message.topics.length > 0 && (
                        <div className="topic-pills">
                          {message.topics.slice(0, 3).map((t) => (
                            <span key={t} className="topic-pill">📎 {TOPIC_LABELS[t] || t}</span>
                          ))}
                        </div>
                      )}
                      <button className="copy-btn" onClick={() => copyMessage(message.text)} title="Copy response" aria-label="Copy response">⧉</button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="message agent">
                <div className="message-avatar">
                  <div className="mini-orb typing-orb">
                    <span className="mini-orb-emoji">🔮</span>
                  </div>
                </div>
                <div className="message-bubble">
                  <div className="typing-indicator">
                    <span>Brij is thinking</span>
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!hasUserMessage && messages.length > 0 && (
              <div className="starter-row" aria-label="Quick questions">
                {STARTERS.map((q) => (
                  <button key={q.label} className="starter-pill" onClick={() => handleStarterClick(q.question)}>
                    {q.label}
                  </button>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {quickReplies.length > 0 && !isTyping && !isStreaming && (
            quickReplies[0]?.variant === 'audience' ? (
              <div className="audience-row" role="group" aria-label="I am a">
                <span className="audience-row-label">I'm a…</span>
                {quickReplies.map((card, i) => (
                  <button key={i} className="audience-chip" onClick={() => handleCardClick(card)} title={card.desc}>
                    <span className="audience-chip-icon" aria-hidden="true">{card.icon}</span>
                    {card.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="quick-replies">
                {quickReplies.map((card, i) => (
                  <button key={i} className="quick-reply-card" onClick={() => handleCardClick(card)}>
                    {card.label}
                  </button>
                ))}
              </div>
            )
          )}

          <div className="chat-input">
            <div className="input-container">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask Brij anything about Dev..."
                className="message-input"
                disabled={isStreaming}
              />
              <button
                onClick={handleSend}
                className="send-btn"
                disabled={!inputValue.trim() || isStreaming}
                aria-label="Send message"
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevarshAgent;
